import dayjs from 'dayjs';
import { notification } from 'antd';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { MissionAdmin, MissionReviewRow, StarUserRef } from './types';
import { MISSION_PERIOD_LABEL, labelOf } from './labels';
import { mapStarError } from './star-error';

const numberFormat = new Intl.NumberFormat('vi-VN');

export function formatNumber(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  return numberFormat.format(num);
}

export function formatDateTime(value?: string | null): string {
  return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—';
}

export function formatDate(value?: string | null): string {
  return value ? dayjs(value).format('DD/MM/YYYY') : '—';
}

export function personName(person?: StarUserRef | null): string {
  return person?.nickName || person?.username || person?.email || (person?.id ? `#${person.id}` : '—');
}

// Ảnh trả về từ POST /image/storages là khoá tương đối; ảnh cũ có thể là URL đầy đủ.
export function imageUrl(image?: string | null): string {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('data:')) return image;
  return `${SERVER_FILE_ENDPOINT}/${image.replace(/^\//, '')}`;
}

// Ô nhập KC chấp nhận "150000", "150.000" hoặc "150,000".
export function parseKcInput(raw?: string | number | null): number | null {
  if (raw === null || raw === undefined) return null;
  const digits = String(raw).replace(/[^\d]/g, '');
  if (!digits) return null;
  return Number(digits);
}

// Danh sách phân trang: { data, metadata: { total } } hoặc mảng trần.
export function extractList<T>(res: any): { items: T[]; total: number } {
  const body = res?.data;
  if (Array.isArray(body)) return { items: body, total: body.length };
  const candidate = body?.data ?? body?.items ?? body?.rows ?? [];
  const items: T[] = Array.isArray(candidate) ? candidate : [];
  const total = body?.metadata?.total ?? body?.meta?.total ?? body?.total ?? items.length;
  return { items, total: Number(total) || 0 };
}

export function extractArray<T>(res: any): T[] {
  return extractList<T>(res).items;
}

export interface BulkResult {
  ok: number;
  failed: { id: number; message: string }[];
}

// Chạy tuần tự một thao tác cho nhiều bản ghi và gom kết quả để báo một lần.
export async function runBulk(
  ids: number[],
  action: (id: number) => Promise<unknown>
): Promise<BulkResult> {
  const result: BulkResult = { ok: 0, failed: [] };
  for (const id of ids) {
    try {
      await action(id);
      result.ok += 1;
    } catch (err) {
      result.failed.push({ id, message: mapStarError(err) });
    }
  }
  return result;
}

export function notifyBulk(result: BulkResult, verb: string) {
  if (result.failed.length === 0) {
    notification.success({ message: `${verb} ${result.ok} bản ghi.` });
    return;
  }
  notification.warning({
    message: `${verb} ${result.ok}/${result.ok + result.failed.length} bản ghi.`,
    description: result.failed.map((f) => `#${f.id}: ${f.message}`).join('\n'),
    duration: 8,
  });
}

export const TABLE_EMPTY = { emptyText: 'Chưa có dữ liệu.' };

// ——— Giới hạn nhiệm vụ nộp tay — docs/nghien-cuu-diem-sao/10 §4.1, §7.1 ———

type MissionLimitSource = Pick<MissionAdmin, 'period' | 'maxRewardsPerPeriod'> &
  Partial<Pick<MissionAdmin, 'maxPerDay' | 'maxPerWeek'>>;

/**
 * Chuỗi gọn cho cột "Giới hạn" / dòng mẫu: "15/tháng · 1/ngày · 2/tuần". Bỏ phần null; không có gì → "—".
 * Kỳ Một lần: "1 lần" (không viết "1/một lần").
 */
export function missionLimitText(mission: MissionLimitSource): string {
  const parts: string[] = [];
  const perPeriod = mission.maxRewardsPerPeriod;
  if (perPeriod !== null && perPeriod !== undefined) {
    parts.push(
      mission.period === 'ONCE'
        ? `${perPeriod} lần`
        : `${perPeriod}/${labelOf(MISSION_PERIOD_LABEL, mission.period).toLowerCase()}`
    );
  }
  if (mission.maxPerDay) parts.push(`${mission.maxPerDay}/ngày`);
  if (mission.maxPerWeek) parts.push(`${mission.maxPerWeek}/tuần`);
  return parts.length ? parts.join(' · ') : '—';
}

type ReviewLimitSource = Pick<
  MissionReviewRow,
  | 'attempt'
  | 'maxRewardsPerPeriod'
  | 'rejectCount'
  | 'submissionsToday'
  | 'monthPaid'
  | 'monthCap'
  | 'countsToCap'
>;

/** "Lần 3/15 · bị từ chối 1 lần" — null khi máy chủ cũ không trả. */
export function reviewAttemptText(r: ReviewLimitSource): string | null {
  const parts: string[] = [];
  if (r.attempt !== undefined) {
    parts.push(
      r.maxRewardsPerPeriod !== null && r.maxRewardsPerPeriod !== undefined
        ? `Lần ${r.attempt}/${r.maxRewardsPerPeriod}`
        : `Lần ${r.attempt}`
    );
  }
  if (r.rejectCount) parts.push(`bị từ chối ${r.rejectCount} lần`);
  return parts.length ? parts.join(' · ') : null;
}

/** Có trần tháng và người này đã chạm (duyệt sẽ không cộng Sao) — doc 10 §4.3 `capped`. */
export function reviewCapReached(r: ReviewLimitSource): boolean {
  if (r.countsToCap === false) return false;
  if (!r.monthCap || r.monthPaid === undefined) return false;
  return r.monthPaid >= r.monthCap;
}

/**
 * "cùng ngày nộp 2 báo cáo · tháng này đã nhận 80/150 Sao nộp tay" — ẩn vế trần khi trần = 0.
 * `submissionsToday` là số báo cáo người đó gửi trong NGÀY VN CỦA LẦN NỘP mới nhất (doc 10 §6.2),
 * không phải hôm nay — báo cáo tối qua mở sáng nay vẫn ghi theo ngày nộp.
 */
export function reviewQuotaText(r: ReviewLimitSource): string | null {
  const parts: string[] = [];
  if (r.submissionsToday !== undefined) {
    parts.push(`cùng ngày nộp ${r.submissionsToday} báo cáo`);
  }
  if (r.monthPaid !== undefined && r.monthCap) {
    parts.push(`tháng này đã nhận ${r.monthPaid}/${r.monthCap} Sao nộp tay`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export const CAP_REACHED_WARNING = 'đã chạm trần tháng — duyệt sẽ không cộng Sao';
